import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useToaster } from '../context/ToasterContext';
import api from '../api/axios';

export function useSenders({ activeAccountId, watchSenders, setValue, watchRoutingStrategy }) {
  const { toaster } = useToaster();

  // Connectors already fetched globally — use them to resolve ConnectorType per sender
  const connectors = useSelector(s => s.globalData.connectors) || [];

  const [showModal, setShowModal] = useState(false);
  const [selectedSenderId, setSelectedSenderId] = useState('');
  const [selectedSenderData, setSelectedSenderData] = useState(null);
  const [unassignedSenders, setUnassignedSenders] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);

  // Maps connectorType -> senderId for the primary sender per type (ROUND_ROBIN only)
  const [roundRobinPrimaries, setRoundRobinPrimaries] = useState({});

  // ROUND_ROBIN  = one primary per ConnectorType; each type gets an equal 1/N share
  // ROUND_ROBIN_BY_NUMBER = even split across all active senders
  const isRoundRobin         = watchRoutingStrategy === 'ROUND_ROBIN';
  const isRoundRobinByNumber = watchRoutingStrategy === 'ROUND_ROBIN_BY_NUMBER';

  // Derive selected sender preview
  useEffect(() => {
    if (!selectedSenderId) {
      setSelectedSenderData(null);
    } else {
      setSelectedSenderData(unassignedSenders.find(s => s.id === selectedSenderId) || null);
    }
  }, [selectedSenderId, unassignedSenders]);

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  /** Resolve connectorType via vendorAccountId lookup against the Redux connectors list. */
  const getSenderConnectorType = useCallback((sender) => {
    if (!sender?.vendorAccountId || !connectors.length) return 'Unknown';
    const connector = connectors.find(c => c.id === sender.vendorAccountId);
    return connector?.connectorType || 'Unknown';
  }, [connectors]);

  /**
   * ROUND_ROBIN_BY_NUMBER — even split across all *active* senders.
   * Last active sender absorbs any floating-point remainder so the total is exactly 1.
   */
  const rebalanceEvenSplit = useCallback((senders) => {
    const activeCount = senders.filter(s => s.active).length;
    if (activeCount === 0) return senders.map(s => ({ ...s, percentage: 0 }));
    const share = +(1 / activeCount).toFixed(6);
    let assigned = 0;
    return senders.map((s) => {
      if (!s.active) return { ...s, percentage: 0 };
      assigned += 1;
      const pct = assigned === activeCount
        ? +(1 - share * (activeCount - 1)).toFixed(6)
        : share;
      return { ...s, percentage: pct };
    });
  }, []);

  /**
   * ROUND_ROBIN — each unique ConnectorType gets an equal 1/N share.
   * Within a type, only the primary sender gets that share; all others get 0.
   * Single-sender types are automatically primary (no explicit entry needed in primaries).
   */
  const rebalanceByType = useCallback((senders, primaries) => {
    if (!senders?.length) return senders;

    const typeGroups = {};
    senders.forEach(s => {
      const type = getSenderConnectorType(s);
      if (!typeGroups[type]) typeGroups[type] = [];
      typeGroups[type].push(s.id);
    });

    const knownTypes = Object.keys(typeGroups).filter(t => t !== 'Unknown');
    const numTypes = knownTypes.length;
    if (numTypes === 0) return senders.map(s => ({ ...s, percentage: 0 }));

    const share = +(1 / numTypes).toFixed(6);
    const typePercentages = {};
    knownTypes.forEach((type, i) => {
      typePercentages[type] = i === knownTypes.length - 1
        ? +(1 - share * (numTypes - 1)).toFixed(6)
        : share;
    });

    return senders.map(sender => {
      const type = getSenderConnectorType(sender);
      if (type === 'Unknown') return { ...sender, percentage: 0 };
      // Fall back to first in the group so single-sender types are always primary
      const primaryId = primaries[type] || typeGroups[type][0];
      return {
        ...sender,
        percentage: sender.id === primaryId ? typePercentages[type] : 0,
      };
    });
  }, [getSenderConnectorType]);

  /**
   * Build an initial primaries map from the current senders list.
   * - Single-sender type  → that sender is always primary.
   * - Multi-sender type   → prefer the sender that already carries the highest percentage
   *                         (preserves state when editing an existing template).
   */
  const buildInitialPrimaries = useCallback((senders) => {
    const typeGroups = {};
    senders.forEach(s => {
      const type = getSenderConnectorType(s);
      if (type === 'Unknown') return;
      if (!typeGroups[type]) typeGroups[type] = [];
      typeGroups[type].push(s);
    });

    const primaries = {};
    Object.entries(typeGroups).forEach(([type, group]) => {
      if (group.length === 1) {
        primaries[type] = group[0].id;
      } else {
        const withPct = group.filter(s => +s.percentage > 0);
        primaries[type] = withPct.length > 0
          ? withPct.reduce((a, b) => (+a.percentage >= +b.percentage ? a : b)).id
          : group[0].id;
      }
    });
    return primaries;
  }, [getSenderConnectorType]);

  // ------------------------------------------------------------------
  // Strategy-switch effects
  // ------------------------------------------------------------------

  const prevStrategyRef = useRef(watchRoutingStrategy);

  useEffect(() => {
    const prev = prevStrategyRef.current;
    prevStrategyRef.current = watchRoutingStrategy;
    if (prev === watchRoutingStrategy) return;

    if (isRoundRobinByNumber && watchSenders.length > 0) {
      // Even split — no primaries needed
      setValue('senders', rebalanceEvenSplit([...watchSenders]), { shouldValidate: true });
      setRoundRobinPrimaries({});
    }
    // ROUND_ROBIN init is handled by the connectors effect below (connectors may still be loading)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchRoutingStrategy]);

  /**
   * Initialise ROUND_ROBIN primaries once connectors are available.
   * Runs when:  strategy === ROUND_ROBIN  AND  connectors finish loading.
   * Skips if primaries are already populated (user has already made selections).
   */
  useEffect(() => {
    if (!isRoundRobin) return;
    if (!connectors.length) return;
    if (!watchSenders.length) return;
    if (Object.keys(roundRobinPrimaries).length > 0) return;

    const primaries = buildInitialPrimaries(watchSenders);
    setRoundRobinPrimaries(primaries);
    setValue('senders', rebalanceByType(watchSenders, primaries), { shouldValidate: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoundRobin, connectors]);

  // ------------------------------------------------------------------
  // ROUND_ROBIN primary selection (called from SendersPanel radio buttons)
  // ------------------------------------------------------------------

  const setPrimaryForType = useCallback((connectorType, senderId) => {
    const newPrimaries = { ...roundRobinPrimaries, [connectorType]: senderId };
    setRoundRobinPrimaries(newPrimaries);
    setValue('senders', rebalanceByType(watchSenders, newPrimaries), { shouldValidate: true });
  }, [roundRobinPrimaries, watchSenders, rebalanceByType, setValue]);

  // ------------------------------------------------------------------
  // Fetch unassigned senders
  // ------------------------------------------------------------------

  const fetchUnassignedSenders = useCallback(async () => {
    if (!activeAccountId) return;
    setUnassignedLoading(true);
    try {
      const response = await api.get(`/mvapi/utils/senders/account/${activeAccountId}/unassigned`);
      setUnassignedSenders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch unassigned senders:', error);
      toaster.error('Failed to load available senders');
    } finally {
      setUnassignedLoading(false);
    }
  }, [activeAccountId, toaster]);

  // ------------------------------------------------------------------
  // Add / Remove / Update senders
  // ------------------------------------------------------------------

  const openAddSenderModal = () => {
    setSelectedSenderId('');
    setSelectedSenderData(null);
    fetchUnassignedSenders();
    setShowModal(true);
  };

  const addSender = () => {
    if (!selectedSenderId) {
      toaster.error('Please select a sender!');
      return;
    }

    const selected = unassignedSenders.find(s => s.id === selectedSenderId);
    if (!selected) return;

    const current = watchSenders.slice();

    if (current.some(s => s.id === selectedSenderId)) {
      toaster.error('This sender is already added');
      return;
    }

    const isFirst = current.length === 0;

    const newSender = {
      accountId:       selected.accountId || activeAccountId || '',
      id:              selected.id,
      name:            selected.name,
      notes:           selected.notes,
      type:            selected.type,
      active:          !!selected.active,
      defaultNumber:   isFirst,
      number:          selected.number || '',
      percentage:      isFirst ? 1 : 0,
      rate:            selected.rate || 0,
      vendorAccountId: selected.vendorAccountId,
      smsEnabled:      !!selected.smsEnabled,
      mmsEnabled:      !!selected.mmsEnabled,
      whatsAppEnabled: !!selected.whatsAppEnabled,
    };

    if (newSender.defaultNumber) {
      current.forEach(s => { s.defaultNumber = false; });
    }

    const updated = [...current, newSender];

    if (isRoundRobin) {
      // If new sender is the first in its ConnectorType, auto-select it as primary
      const newType = getSenderConnectorType(newSender);
      const newPrimaries = { ...roundRobinPrimaries };
      if (newType !== 'Unknown' && !newPrimaries[newType]) {
        newPrimaries[newType] = newSender.id;
        setRoundRobinPrimaries(newPrimaries);
      }
      setValue('senders', rebalanceByType(updated, newPrimaries), { shouldValidate: true });
    } else if (isRoundRobinByNumber) {
      setValue('senders', rebalanceEvenSplit(updated), { shouldValidate: true });
    } else {
      setValue('senders', updated, { shouldValidate: true });
    }

    setSelectedSenderId('');
    setShowModal(false);
  };

  const removeSender = async (index) => {
    const next = watchSenders.slice();
    const removedWasDefault = next[index]?.defaultNumber;
    const removedSender = next[index];

    if (removedSender?.id && activeAccountId) {
      try {
        console.debug(`Removing Sender: ${removedSender.id}`);
        await api.patch(
          `/mvapi/utils/senders/account/${activeAccountId}/sender/${removedSender.id}/remove-template`
        );
      } catch (error) {
        console.error('Failed to clear templateId from sender:', error);
        toaster.error('Failed to unassign sender — please try again.');
        return;
      }
    }

    next.splice(index, 1);

    if (removedWasDefault && next.length === 1) {
      next[0].defaultNumber = true;
    }

    if (isRoundRobin) {
      // If the removed sender was primary for its type, promote the next sender in that group
      const newPrimaries = { ...roundRobinPrimaries };
      Object.entries(newPrimaries).forEach(([type, primaryId]) => {
        if (primaryId === removedSender.id) {
          const remaining = next.filter(s => getSenderConnectorType(s) === type);
          if (remaining.length > 0) {
            newPrimaries[type] = remaining[0].id;
          } else {
            delete newPrimaries[type];
          }
        }
      });
      setRoundRobinPrimaries(newPrimaries);
      setValue('senders', rebalanceByType(next, newPrimaries), { shouldValidate: true });
    } else if (isRoundRobinByNumber) {
      setValue('senders', rebalanceEvenSplit(next), { shouldValidate: true });
    } else {
      setValue('senders', next, { shouldValidate: true });
    }
  };

  const updateSender = (index, field, value) => {
    const next = watchSenders.slice();

    if (field === 'defaultNumber' && value === true) {
      next.forEach((s, i) => { if (i !== index) s.defaultNumber = false; });
    }

    if (field === 'active' && value === false && next[index].defaultNumber) {
      next[index].defaultNumber = false;
    }

    next[index][field] = value;

    // ROUND_ROBIN_BY_NUMBER: rebalance even split when active status changes
    if (field === 'active' && isRoundRobinByNumber) {
      setValue('senders', rebalanceEvenSplit(next), { shouldValidate: true });
      return;
    }

    // ROUND_ROBIN: percentages are managed only via setPrimaryForType (radio buttons)
    setValue('senders', next, { shouldValidate: true });
  };

  const unassignedSenderOptions = unassignedSenders.map(s => ({ value: s.id, label: s.name }));

  return {
    showModal,
    setShowModal,
    selectedSenderId,
    setSelectedSenderId,
    selectedSenderData,
    unassignedSenderOptions,
    unassignedLoading,
    openAddSenderModal,
    addSender,
    removeSender,
    updateSender,
    roundRobinPrimaries,
    setPrimaryForType,
    getSenderConnectorType,
  };
}
